import * as React from "react";
import { Link } from "react-router-dom";
import {
  BotMessageSquare,
  CalendarClock,
  CalendarRange,
  Link2,
  PlugZap,
  Users,
} from "lucide-react";

import {
  getConnectionSummary,
  listBookingPages,
  listConnections,
  listIntegrations,
  listAvailabilityPatterns,
} from "@/api/client";
import {
  BookingPageView,
  ConnectionView,
  IntegrationsView,
  AvailabilityPatternView,
} from "@/api/types";
import { PageTitle } from "@/components/PageTitle";
import { ProfileProgress, ProgressStep } from "@/features/profile/ProfileProgress";
import { useFeature } from "@/features/profile/useFeature";
import { cn } from "@/lib/utils";
import { activeProviders, availableProviders } from "@/lib/calendarProviders";

/**
 * Home: one overview of everything the user has set up and everything currently
 * happening, for every tier.
 *
 * <p>This replaced the A2A chat as {@code /}. Landing straight in the chat meant a
 * free-tier user's first screen was a feature they can't use, and it took a guess
 * ({@code HomeGate} redirected on connection count) to decide where anyone belonged.
 * Home is the same page for everyone and links OUT to the chat and booking pages, so
 * tier only changes what a card says — never whether you have somewhere to land.</p>
 *
 * <p>Each card leads with a count and then names a few real items, so the page shows
 * your actual configuration rather than totals. New features get a card here.</p>
 */
export function OverviewPage() {
  const [pages, setPages] = React.useState<BookingPageView[] | null>(null);
  const [types, setTypes] = React.useState<AvailabilityPatternView[] | null>(null);
  const [integrations, setIntegrations] = React.useState<IntegrationsView | null>(null);
  const [friends, setFriends] = React.useState<ConnectionView[] | null>(null);
  const [pending, setPending] = React.useState(0);
  // Was a hardcoded tier list here (PRO || ENTERPRISE) plus its own entitlements fetch. Asking for
  // the capability instead means an admin changing which tiers get chat is reflected here without
  // a deploy, and the shared hook means the nav and this card cost one request between them.
  const chatEntitled = useFeature("AI_SCHEDULING") !== "denied";

  React.useEffect(() => {
    // Each card degrades on its own — one failing endpoint must not blank the page.
    void listBookingPages().then(setPages).catch(() => setPages([]));
    void listAvailabilityPatterns().then(setTypes).catch(() => setTypes([]));
    void listIntegrations().then(setIntegrations).catch(() => setIntegrations(null));
    void listConnections("accepted").then(setFriends).catch(() => setFriends([]));
    void getConnectionSummary().then((s) => setPending(s.pending)).catch(() => {});
  }, []);

  const live = (pages ?? []).filter((p) => !isEnded(p) && p.status === "ACTIVE");
  const ended = (pages ?? []).filter(isEnded);
  const googleLinked =
    activeProviders(integrations ?? null).length > 0;

  const steps: ProgressStep[] = [
    { label: "Add your availability patterns", done: (types?.length ?? 0) > 1 },
    { label: "Share a booking link", done: (pages?.length ?? 0) > 0 },
    { label: "Connect a calendar", done: googleLinked },
    { label: "Connect with someone", done: (friends?.length ?? 0) > 0 },
  ];

  return (
    <main className="mx-auto max-w-[1140px] space-y-5 px-6 py-8 font-comic-body">
      <PageTitle>Your Scheda home</PageTitle>

      <ProfileProgress steps={steps} />

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        <Card
          icon={<Link2 className="h-4 w-4" />}
          title="Booking links"
          // Straight into the expanded live-bookings view, not the builder.
          to="/booking-pages?view=live"
          action="Open your bookings"
          count={pages === null ? null : live.length}
          countLabel={live.length === 1 ? "live link" : "live links"}
        >
          {pages !== null && (
            <>
              {ended.length > 0 && (
                <p className="text-sm text-foreground">{ended.length} ended</p>
              )}
              <ItemList
                items={live.slice(0, 3).map((p) => ({
                  key: p.id,
                  main: p.title,
                  sub:
                    p.max_bookings != null
                      ? `${p.booking_count}/${p.max_bookings} booked`
                      : `${p.booking_count} booked`,
                }))}
                empty="No links yet. Create one to get booked."
              />
            </>
          )}
        </Card>

        <Card
          icon={<CalendarRange className="h-4 w-4" />}
          title="Availability patterns"
          to="/profile#availability-patterns"
          action="Edit availability"
          count={types === null ? null : types.length}
          countLabel={types?.length === 1 ? "type" : "types"}
        >
          <ItemList
            items={(types ?? []).slice(0, 3).map((t) => ({
              key: t.id,
              main: t.is_default ? `${t.name} ★` : t.name,
              sub: rangeSummary(t),
            }))}
            empty="No availability patterns yet."
          />
        </Card>

        {/* Always to /connections, whatever the plan. Making a new connection is Pro; seeing the
            ones you already have — and removing one — is not, because V35 gave people connections
            they never agreed to. This card used to send unentitled hosts to /upgrade instead, which
            left exactly those people with no route to the page. */}
        <Card
          icon={<Users className="h-4 w-4" />}
          title="Friends"
          to="/connections"
          action="View connections"
          count={friends === null ? null : friends.length}
          countLabel={friends?.length === 1 ? "connection" : "connections"}
        >
          {pending > 0 && (
            <p className="text-sm font-semibold text-brand-blue">{pending} awaiting your reply</p>
          )}
          <ItemList
            items={(friends ?? []).slice(0, 3).map((c) => ({
              key: c.id,
              main: c.peerDisplayName ?? (c.peerHandle ? `@${c.peerHandle}` : "Someone"),
              sub: c.peerHandle ? `@${c.peerHandle}` : "",
            }))}
            empty={
              chatEntitled
                ? "No connections yet. Connect with someone so your agent can schedule with them."
                : "Connections let your agent schedule with people directly."
            }
          />
        </Card>

        <Card
          icon={<PlugZap className="h-4 w-4" />}
          title="Integrations"
          to="/profile"
          action="Manage"
          count={null}
        >
          {integrations === null ? (
            <p className="text-sm text-foreground">Couldn't load.</p>
          ) : availableProviders(integrations).length === 0 ? (
            <p className="text-sm text-foreground">
              Calendar linking isn't enabled on this deployment.
            </p>
          ) : (
            <p className="text-sm text-foreground">
              <span className={cn("font-semibold", googleLinked ? "text-brand-blue" : "text-foreground")}>
                Google Calendar
              </span>{" "}
              <span>{googleLinked ? "· connected" : "· not connected"}</span>
            </p>
          )}
        </Card>

        <Card
          icon={<BotMessageSquare className="h-4 w-4" />}
          title="Agent chat"
          // Non-entitled users go to /upgrade rather than into a feature they can't use.
          to={chatEntitled ? "/chat" : "/upgrade"}
          action={chatEntitled ? "Open the chat" : "See plans"}
          count={null}
          badge={chatEntitled ? undefined : "Pro"}
        >
          <p className="text-sm text-foreground">
            Ask your agent to find a time with someone you're connected to. It negotiates and books.
          </p>
        </Card>

        <Card
          icon={<CalendarClock className="h-4 w-4" />}
          title="Calendar"
          to="/meetings"
          action="Open calendar"
          count={null}
        >
          <p className="text-sm text-foreground">
            Everything booked and negotiated, on one calendar.
          </p>
        </Card>
      </div>
    </main>
  );
}

/** A link is finished once ends_at passes — that already folds in valid_until. */
function isEnded(p: BookingPageView): boolean {
  return p.ends_at != null && Date.parse(p.ends_at) < Date.now();
}

/**
 * "Mon–Fri 09:00–17:00" style gist of a type's FIRST range — enough to see the
 * configuration at a glance, with "+N" standing in for the rest.
 */
function rangeSummary(type: AvailabilityPatternView): string {
  if (type.ranges.length === 0) return "no hours set";
  const first = type.ranges[0];
  const days = first.days.map((d) => d.slice(0, 3)).join(", ");
  const more = type.ranges.length > 1 ? ` +${type.ranges.length - 1}` : "";
  return `${days || "no days"} ${first.earliest}–${first.latest}${more}`;
}

function Card({
  icon,
  title,
  to,
  action,
  count,
  countLabel,
  badge,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  to: string;
  action: string;
  count: number | null;
  countLabel?: string;
  badge?: string;
  children?: React.ReactNode;
}) {
  return (
    <section className="flex flex-col rounded-2xl border border-foreground bg-card p-4 comic-shadow-sm">
      <header className="flex items-center gap-2">
        <span className="text-muted-foreground">{icon}</span>
        <h2 className="font-comic-headline text-sm uppercase tracking-wide">{title}</h2>
        {badge && (
          <span className="rounded-full border border-amber-400 bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
            {badge}
          </span>
        )}
      </header>

      {count !== null && (
        <p className="mt-1 font-comic-headline text-2xl leading-none">
          {count}
          {countLabel && (
            <span className="ml-1 align-middle text-sm font-medium text-foreground">
              {countLabel}
            </span>
          )}
        </p>
      )}

      <div className="mt-2 flex-1 space-y-1">{children}</div>

      <Link
        to={to}
        className="mt-3 inline-flex w-fit items-center gap-1 rounded-full border border-brand-blue/50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand-blue transition hover:border-brand-blue hover:bg-brand-blue/10"
      >
        {action} →
      </Link>
    </section>
  );
}

function ItemList({
  items,
  empty,
}: {
  items: { key: string; main: string; sub: string }[];
  empty: string;
}) {
  if (items.length === 0) {
    return <p className="text-sm italic text-muted-foreground">{empty}</p>;
  }
  return (
    <ul className="space-y-1">
      {items.map((i) => (
        <li key={i.key} className="flex items-baseline justify-between gap-2 text-sm">
          <span className="truncate font-medium text-foreground">{i.main}</span>
          <span className="shrink-0 text-foreground">{i.sub}</span>
        </li>
      ))}
    </ul>
  );
}
